import { defineStore } from 'pinia';
import { useMediaItemsStore } from './mediaItem';
import intlsplitValidation from '@/views/contract/js/intlsplitValidation';
import contractnet from '@/network/contractnet';
import basicnet from '@/network/optional.js';
import { useContractStore } from './contract';
import { useContractInfoStore } from './contractinfo';
import { useOperationStore } from './operation';
import { convertDecimal } from '@/assets/js/convert';
import validation from '@/assets/js/validation.js';
import { v4 as uuidv4 } from 'uuid';

const IntlSplitRules = require('@/views/contract/js/internalSplitRules.js');

const NumberJs = require('@/assets/js/numberjs.js');
const contractSplitMediaJs = require('@/models/contractSplitMedia');
const dayjs = require('dayjs');

export const useInternalSplitStore = defineStore('internalsplit', {
  state: () => ({
    ContractId: 0,
    // 当前编辑的媒体
    EditIndex: -1,
    // 分拆摘要
    ContractSplit: {},
    // 分拆媒体
    ContractSplitMedias: [],
    // 分拆制作费
    ContractProductionSplits: [],
    // 当前编辑中的分拆制作费
    ProductionSplit: {},
    // 当前编辑中的分拆制作费
    CostTypeList: [],
    // 特殊佣金安排列表
    SpecialCommissionList: [],
    // 当前处理特殊佣金
    SpecialCommission: {},
    // 分拆信息验证状态
    ContractSplitValidation: [],
    // 支付费用类型
    ProductionCostOptions: [],
    // 销售服务类型
    SalesServiceTypeOptions: [],
    //默认编辑中的分拆制作费
    CostTypeListTmp: [],
    //当前编辑的媒体SplitMedialId:
    SplitMedialId: '',
    MediaList: [],
    //设定制作费用
    ProductionCostTypes: [],
    ProductionCostSplitField: [],
  }),
  getters: {
    // 显示是否包含代理费(Y/N)
    AgencyText(state) {
      return state.ContractSplit.isAgency ? 'Y' : 'N';
    },
    // 显示是否新客户(Y/N)
    NewClientText(state) {
      return state.ContractSplit.isNewClient ? 'Y' : 'N';
    },
    // 显示是否慈善机构(Y/N)
    CharityText(state) {
      return state.ContractSplit.isCharity ? 'Y' : 'N';
    },
    // 预付制作费结束日期验证高亮显示
    additionalProductionEndDateValidation(state) {
      return (
        state.ContractSplitValidation.filter(
          (t) => t.field === 'additionalProductionEndDate'
        ).length > 0
      );
    },
    // 制作费类型验证高亮显示
    productionCostTypeValidation(state) {
      return (
        state.ContractSplitValidation.filter(
          (t) => t.field === 'productionCostType'
        ).length > 0
      );
    },
    // 服务费类型验证高亮显示
    salesServiceTypeValidation(state) {
      return (
        state.ContractSplitValidation.filter(
          (t) => t.field === 'salesServiceType'
        ).length > 0
      );
    },
  },
  actions: {
    ContractSplitEntity(entity) {
      return {
        Id: entity.id,
        ContractId: entity.contractId,
        // 合同金额摘要
        ContractMediaAmount: entity.contractMediaAmount,
        ContractProductionAmount: entity.contractProductionAmount,
        ContractTotalAmount: entity.contractTotalAmount,
        // 合同媒体金额摘要
        GrossMediaTotal: entity.grossMediaTotal,
        MediaBonusTotal: entity.mediaBonusTotal,
        ProductionTotal: entity.productionTotal,
        ProductionBonusTotal: entity.productionBonusTotal,
        // 特殊佣金安排摘要
        SpecialComMediaAmount: entity.specialComMediaAmount,
        SpecialComProductionAmount: entity.specialComProductionAmount,
        // 合同计算摘要
        MediaNett: entity.mediaNett,
        PICost: entity.piCost,
        ProductionCostType: entity.productionCostType,
        SalesServiceFee: entity.salesServiceFee,
        SalesServiceType: entity.salesServiceType,
        SalesServiceOther: entity.salesServiceOther,
        AdditionalProduction: entity.additionalProduction,
        AdditionalProductionEndDate: entity.additionalProductionEndDate,
        ProductionTotalWithBonus: entity.productionTotalWithBonus,
        GrossMediaTotalWithBonus: entity.grossMediaTotalWithBonus,
        DiscountTotalWithBonus: entity.discountTotalWithBonus,
        DiscountRateWithBonus: entity.discountRateWithBonus,
        DiscountTotalWithBonusSA: entity.discountTotalWithBonusSA,
        DiscountRateWithBonusSA: entity.discountRateWithBonusSA,
        DiscountTotal: entity.discountTotal,
        DiscountRate: entity.discountRate,
        DiscountTotalSA: entity.discountTotalSA,
        DiscountRateSA: entity.discountRateSA,
        IsAgency: entity.isAgency,
        IsCharity: entity.isChrity,
        IsNewClient: entity.isNewClient,
        SpecialDiscountAmount: entity.specialDiscountAmount,
        SpecialDiscountDesc: entity.specialDiscountDesc,
      };
    },
    /**
     * 刷新页面
     * @param {Number} ContractId 合同Id
     */
    async RefreshInternalSplit() {
      const useContract = useContractStore();
      const ContractId = useContract.Id;

      await this.getContractSplit(ContractId);

      // 制作费分拆列表
      await this.getProductionSplitList(ContractId);

      // Special Commission
      await this.getSpecialCommissionList(ContractId);

      // 初始化编辑状态
      this.EditIndex = -1;
    },
    async getContractSplit(ContractId) {
      // Split
      const split_res = await contractnet
        .ContractInternalSplit(0, ContractId)
        .then((res) => res);

      this.ContractSplit = split_res.data.split;
      this.ContractSplitMedias = split_res.data.splitMedias;

      this.MediaList = split_res.data.mediaList;
      this.ProductionCostTypes = split_res.data.productionCostTypes;
      if (split_res.data.productionCostSplitField) {
        this.ProductionCostSplitField = JSON.parse(
          split_res.data.productionCostSplitField
        );
      }
      this.initProductionSplits();
      this.ContractSplitMedias.map((t) => {
        t.dates = [
          dayjs(t.startDate).format('YYYY-MM-DD'),
          dayjs(t.endDate).format('YYYY-MM-DD'),
        ];
      });
    },
    initProductionSplits() {
      if (this.ContractSplitMedias && this.ContractSplitMedias.length > 0) {
        this.ContractSplitMedias.map((t) => {
          if (!t.productionSplit) {
            t.productionSplit = {};
            t.productionSplit.splitDetailId = t.id;
            t.productionSplit.contractId = t.contractId;
            t.productionSplit.contractMediaId = t.contractMediaId;
          }
          t.productionSplit.prod_cost_split = this.ProductionCostTypes;
          if (t.productionSplit.productionCostSplit) {
            t.productionSplit.prod_cost_split = JSON.parse(
              t.productionSplit.productionCostSplit
            ).map((o) => this.NewProductionSplit(o));
          }
        });
      }
    },
    getContractSplitMedia(guid) {
      return this.ContractSplitMedias.find(
        (t) => t.id === guid || t.splitMediaId === guid
      );
    },
    /**
     * 特殊佣金安排列表
     * @param {*} ContractId
     */
    async getSpecialCommissionList(ContractId) {
      const res = await contractnet
        .getSpecialCommissionArrangementList(ContractId)
        .then((res) => res.data);
      this.SpecialCommissionList = res;
    },
    /**
     * 获取Special Commission
     * @param {*} SplitMediaId 分拆明细Id
     * @param {*} NettItemSum 媒体最后金额
     * @param {*} TotalProductionCost 制作费
     */
    getSpecialCommission(SplitMediaId) {
      const special =
        this.SpecialCommissionList.find(
          (t) => t.splitMediaId === SplitMediaId
        ) || null;

      this.SpecialCommission.mediaCost = special?.mediaCost;
      this.SpecialCommission.productionCost = special?.productionCost;
      this.SpecialCommission.id = special?.id || 0;
      this.SpecialCommission.splitDetailId = special?.splitDetailId ?? 0;
      this.SpecialCommission.splitMediaId = SplitMediaId;
      this.SpecialCommission.contractId = this.ContractSplit.contractId;
    },
    /**
     * 制作费分拆列表
     * @param {Number} ContractId 合同Id
     */
    async getProductionSplitList(ContractId) {
      const result = await contractnet
        .getProductionSplitList(ContractId)
        .then((res) => res.data);

      this.ContractProductionSplits = result.producionSplits;
      this.CostTypeListTmp = result.costTypeList;
    },
    getProductionCostSplit(splitMediaId) {
      return {
        ...this.ContractProductionSplits.find(
          (t) => t.splitMediaId == splitMediaId
        ),
      };
    },
    setProductionCostSplit(data) {
      var productionCost = this.ContractProductionSplits.find(
        (t) => t.prodSplit_Id == data.prodSplit_Id
      );

      if (!productionCost) {
        this.ContractProductionSplits.push({ prodSplit_Id: uuidv4() });
        productionCost =
          this.ContractProductionSplits[
            this.ContractProductionSplits.length - 1
          ];
      }

      productionCost = Object.assign(productionCost, data);
      console.log('set', data, { ...productionCost }, [
        ...this.ContractProductionSplits,
      ]);
    },
    /**
     *当前编辑中的分拆制作费
     * @param {*} index
     */
    getProductionSplit(SplitMedialId) {
      //是否当前编辑媒体
      if (this.SplitMedialId == SplitMedialId) {
        return;
      }
      this.SplitMedialId = SplitMedialId;
      // 分拆媒体信息
      const lSplitMedia = this.ContractSplitMedias.find(
        (t) => t.splitMediaId === SplitMedialId
      );

      const lProductionSplit =
        this.ContractProductionSplits.find(
          (t) => t.splitMediaId == SplitMedialId
        ) || null;

      if (
        // parseFloat(lProductionSplit?.totalCost || 0) !==
        // parseFloat(lSplitMedia.unitProductionCost) ||
        lProductionSplit === null
      ) {
        this.ProductionSplit.id = 0;
        this.ProductionSplit.productionCost = lSplitMedia.unitProductionCost;
        this.ProductionSplit.totalCost = lSplitMedia.unitProductionCost;
        this.CostTypeList = JSON.parse(JSON.stringify(this.CostTypeListTmp));
      } else {
        this.ProductionSplit.id = lProductionSplit.id;
        this.ProductionSplit.productionCost = lProductionSplit.productionCost;
        this.ProductionSplit.totalCost = lProductionSplit.totalCost;
        this.CostTypeList = JSON.parse(
          JSON.stringify(lProductionSplit.costTypeList)
        );
      }
      this.ProductionSplit.splitDetailId = lSplitMedia.splitDetailId ?? 0;
      this.ProductionSplit.splitMediaId = SplitMedialId;
      this.ProductionSplit.contractId = this.ContractSplit.contractId;
      this.ProductionSplit.contractMediaId = lSplitMedia.contractMediaId;
    },
    /**
     * 主页统一保存行为
     */
    async toSave() {
      const action = 'save';

      // ContractSplit Validation
      await this.validate(undefined, undefined, action);
      if ((this.ContractSplit.validate || []).length > 0) {
        return this.ContractSplit.validate[0].message;
      }

      // ContractSplitMedia Validation
      for (let index = 0; index < this.ContractSplitMedias.length; index++) {
        await this.validate('media', index, action);
      }
      if (
        this.ContractSplitMedias.filter((t) => (t.validate || []).length > 0)
          .length > 0
      ) {
        return this.ContractSplitMedias.filter(
          (t) => (t.validate || []).length > 0
        )[0].validate[0].message;
      }

      // SpecialCommission
      // for (let c = 0; c < this.SpecialCommissionList.length; c++) {
      //   await this.validate('commission', c, action)

      // }
      if (
        this.SpecialCommissionList.filter((t) => (t.validate || []).length > 0)
          .length > 0
      ) {
        return this.SpecialCommissionList.filter(
          (t) => (t.validate || []).length > 0
        )[0].validate[0].message;
      }

      var message = await this.SaveData().then((msg) => msg);

      if (message === '') {
        await this.RefreshInternalSplit();

        const useOperation = useOperationStore();
        useOperation.getProductionChangeSummary(this.ContractSplit.contractId);
      }

      return message;
    },
    /**
     * 保存数据
     */
    async SaveData() {
      const producionSplitJs = require('@/models/productionSplit');
      const specialCommissionJs = require('@/models/specialCommission');

      const RequestInternalSplit = this.ContractSplitEntity(this.ContractSplit);
      const RequestInternalSplitMedias = this.ContractSplitMedias;
      // 制作费分拆信息
      const RequestProductionSplits = this.ContractProductionSplits;
      // 特殊佣金安排
      const RequestSpecialCommissions =
        specialCommissionJs.SaveSpecialCommissionsParam(
          this.SpecialCommissionList
        );

      const useContractInfo = useContractInfoStore();

      const SplitInfo = {
        RequestInternalSplit,
        RequestInternalSplitMedias,
        RequestProductionSplits,
        RequestSpecialCommissions,
        Contract_Id: useContractInfo.Contract.contract_Id,
      };

      const split_res = await contractnet
        .saveContractInternalSplit(SplitInfo)
        .then((res) => res);
      var message = split_res.data.resultMessage || '';

      return message;
    },
    /**
     * 验证保存按钮
     */
    async ValidationSave() {
      const res =
        (await intlsplitValidation
          .ContractSplitVaidation(
            this.ContractSplit,
            this.ContractSplitMedias,
            this.SpecialCommissionList
          )
          .then((res) => res)) || [];

      this.ContractSplitValidation = res;
    },
    /**
     * 更新制作费分拆
     */
    UpdateProductionSplit() {
      const SplitDetailId = this.ProductionSplit.splitDetailId;
      const SplitMediaId = this.ProductionSplit.splitMediaId;

      var SplitMedia = this.getContractSplitMedia(SplitMediaId) || {};
      var ContractProductionSplit =
        this.ContractProductionSplits.find(
          (t) => t.splitMediaId === SplitMediaId
        ) || null;
      if (ContractProductionSplit == null) {
        this.ContractProductionSplits.push({
          id: 0,
          splitDetailId: SplitDetailId,
          splitMediaId: SplitMediaId,
        });

        ContractProductionSplit = this.ContractProductionSplits.find(
          (t) => t.splitMediaId === SplitMediaId
        );
        ContractProductionSplit.contractId = this.ProductionSplit.contractId;
        ContractProductionSplit.contractMediaId =
          this.ProductionSplit.contractMediaId;
      }

      var prod_cost_split = {};
      ContractProductionSplit.productionCost = prod_cost_split.productionCost =
        this.ProductionSplit.productionCost;
      // ContractProductionSplit.repaintingCost = prod_cost_split.repaintingCost = this.ProductionSplit.repaintingCost
      // ContractProductionSplit.dismantleCost = prod_cost_split.dismantleCost = this.ProductionSplit.dismantleCost
      // ContractProductionSplit.repairCost = prod_cost_split.repairCost = this.ProductionSplit.repairCost
      ContractProductionSplit.totalCost = prod_cost_split.totalCost =
        this.ProductionSplit.totalCost;
      ContractProductionSplit.costTypeList = prod_cost_split.costTypeList =
        JSON.parse(JSON.stringify(this.CostTypeList));

      //SplitMedia.prodCostSplit = JSON.stringify(prod_cost_split);
    },
    /**
     * 更新特殊佣金
     */
    UpdateSpecialCommission() {
      const SplitDetailId = this.SpecialCommission.splitDetailId;
      const SplitMediaId = this.SpecialCommission.splitMediaId;

      var SpecialCommissionModel =
        this.SpecialCommissionList.find(
          (t) => t.splitMediaId === SplitMediaId
        ) || null;
      if (SpecialCommissionModel == null) {
        this.SpecialCommissionList.push({
          id: 0,
          splitDetailId: SplitDetailId,
          contractId: this.SpecialCommission.contractId,
          splitMediaId: SplitMediaId,
        });

        SpecialCommissionModel = this.SpecialCommissionList.find(
          (t) => t.splitMediaId === SplitMediaId
        );
      }
      if (
        (this.SpecialCommission.mediaCost || '') === '' &&
        (this.SpecialCommission.productionCost || '') === ''
      ) {
        this.SpecialCommissionList.splice(
          this.SpecialCommissionList.indexOf(SpecialCommissionModel),
          1
        );
        return;
      }

      SpecialCommissionModel.mediaCost = parseFloat(
        this.SpecialCommission.mediaCost
      );
      SpecialCommissionModel.productionCost = parseFloat(
        this.SpecialCommission.productionCost
      );
    },
    /**
     * 重置分拆
     */
    async Regenerate() {
      const useContractMedia = useMediaItemsStore();
      const useContractInfo = useContractInfoStore();

      if (
        !useContractMedia.OrderMediaItems ||
        useContractMedia.OrderMediaItems.length < 1
      ) {
        await useContractMedia.RefreshMediaItems();
      }
      // 重置分拆媒体
      const MediaItems = useContractMedia.OrderMediaItems; // 原来媒体明细
      const SplitMedias = MediaItems.map((entity) => {
        const ContractMediaId = entity.id;
        const data = JSON.parse(JSON.stringify(entity));
        data.id = 0;
        data.contractMediaId = ContractMediaId;
        data.contractMedia_Id = entity.contractMedia_Id;

        if (useContractInfo.Contract.contractType === 'media&production') {
          data.unitProductionCost = null;
        }

        var item = contractSplitMediaJs.ContractSplitMediaEntity(data);
        item.mediaPlan = data.mediaPlan;
        item.mediaSnap = this.MediaList.find(
          (t) => t.id == item.contractMediaId
        )?.mediaSnap;

        return item;
      });
      this.ContractSplitMedias = SplitMedias;
      this.initProductionSplits();

      // 制作费分拆
      this.ContractProductionSplits = [];
      // 特殊佣金安排
      this.SpecialCommissionList = [];

      // 重置拆分
      this.ContractSplit.mediaNett = 0;
      this.ContractSplit.productionCostType = '';
      this.ContractSplit.salesServiceFee = 0;
      this.ContractSplit.salesServiceOther = '';
      this.ContractSplit.salesServiceType = '';
      this.ContractSplit.additionalProduction = 0;
      this.ContractSplit.additionalProductionEndDate = '';

      const useContract = useContractStore();
      const ContractSummary = useContract.ContractSummary;
      this.ContractSplit.isAgency = ContractSummary.isAgencyDiscount;
      this.ContractSplit.agencyDiscountRate =
        ContractSummary.agencyDiscountRate;
      this.ContractSplit.isNewClient = ContractSummary.isNewClient;
      this.ContractSplit.isCharity =
        ContractSummary.businessNature === 'Charity';
      this.ContractSplit.contractId = useContract.Id;
      if (useContract.ContractType === 'production') {
        this.ContractSplit.specialDiscountAmount = 0;
        this.ContractSplit.specialDiscountDesc = '';
      }

      this.totalMediaAmount();

      this.totalProductionAmount();

      if (useContract.Unit.alias === 'BB') {
        if (this.MediaList.length > 0) {
          var totalProductionCosts = this.MediaList.map(
            (t) => t.totalProductionCost
          );
          // 制作费用
          this.ContractSplit.piCost =
            this.ContractSplit.productionTotalWithBonus = Math.round(
              totalProductionCosts.reduce((a, b) => a + b),
              0
            );
        }
      }

      this.totalContractAmount();

      this.totalCommissionAmount();
    },
    /**
     * 计算
     */
    Calculate() {
      var FinalMediaNett = convertDecimal(this.ContractSplit.mediaNett);

      var index = -1;
      const ContractRate =
        this.ContractSplit.mediaNett / this.ContractSplit.grossMediaTotal;
      //收费的媒体数量
      var noBonusCount =
        this.ContractSplitMedias.filter((t) => !t.isBonus).length - 1;
      this.ContractSplitMedias.forEach((item) => {
        item.serviceFee = 0;
        item.nettItemSum = 0;
        item.mediaTotalInternalSplit = 0;
        item.productionTotalInternalSplit = 0;
        if (!item.isBonus) {
          index++;
          if (index === noBonusCount) {
            item.nettItemSum = item.mediaTotalInternalSplit =
              convertDecimal(FinalMediaNett);
            item.mediaTotalInternalSplit = item.nettItemSum;
          } else {
            item.nettItemSum = convertDecimal(item.grossItemSum * ContractRate);
            item.mediaTotalInternalSplit = item.nettItemSum;
            FinalMediaNett = convertDecimal(FinalMediaNett - item.nettItemSum);
          }
        }
        item.productionTotalInternalSplit = (
          convertDecimal(item.nettItemSum) +
          convertDecimal(item.totalProductionCost) -
          convertDecimal(item.productionTotalInternalSplit)
        ).toFixed(2);
      });

      // 更新第一排序ServiceFee
      if (this.ContractSplitMedias?.length > 0) {
        this.ContractSplitMedias[0].serviceFee = convertDecimal(
          this.ContractSplit.salesServiceFee
        );
      }
    },
    /**
     * 计算媒体总金额
     */
    totalMediaAmount() {
      const MediaItems = this.ContractSplitMedias;
      const Medias = MediaItems.filter((t) => !t.isBonus) || [];
      const BonusMedias = MediaItems.filter((t) => t.isBonus) || [];

      const MediaTotalSA =
        Medias.length === 0
          ? 0
          : Medias.map(
              (t) => parseFloat(t.mediaTotalInternalSplit) || 0
            ).reduce((pre, curr) => pre + curr);
      const BonusMediaTotalSA =
        MediaItems.length === 0
          ? 0
          : MediaItems.map(
              (t) => parseFloat(t.mediaTotalInternalSplit) || 0
            ).reduce((pre, curr) => pre + curr);

      // 媒体总金额(除赠送)
      this.ContractSplit.grossMediaTotal = convertDecimal(
        Medias.length === 0
          ? 0
          : Medias.map((t) => parseFloat(t.grossItemSum) || 0).reduce(
              (pre, curr) => pre + curr
            )
      );

      // 媒体总金额(赠送)
      this.ContractSplit.mediaBonusTotal = convertDecimal(
        BonusMedias.length === 0
          ? 0
          : BonusMedias.map((t) => parseFloat(t.grossItemSum)).reduce(
              (pre, curr) => pre + curr
            )
      );

      // 媒体总金额
      this.ContractSplit.grossMediaTotalWithBonus = convertDecimal(
        this.ContractSplit.grossMediaTotal + this.ContractSplit.mediaBonusTotal
      );
      this.ContractSplit.agencyDiscountTotal = convertDecimal(
        parseFloat(this.ContractSplit.mediaNett) /
          ((100 - parseFloat(this.ContractSplit.agencyDiscountRate)) / 100) -
          parseFloat(this.ContractSplit.mediaNett)
      );

      // 折扣金额(除赠送)
      this.ContractSplit.discountTotalWithBonus = convertDecimal(
        this.ContractSplit.grossMediaTotal -
          this.ContractSplit.mediaNett -
          this.ContractSplit.agencyDiscountTotal
      );

      // 折扣金额
      this.ContractSplit.discountTotal = convertDecimal(
        this.ContractSplit.grossMediaTotalWithBonus -
          this.ContractSplit.mediaNett -
          this.ContractSplit.agencyDiscountTotal
      );

      this.ContractSplit.discountRateWithBonus =
        this.ContractSplit.grossMediaTotal === 0
          ? 0.0
          : convertDecimal(
              (this.ContractSplit.discountTotalWithBonus /
                this.ContractSplit.grossMediaTotal) *
                100
            );

      // 折扣
      this.ContractSplit.discountRate =
        this.ContractSplit.grossMediaTotalWithBonus === 0
          ? 0.0
          : convertDecimal(
              (this.ContractSplit.discountTotal /
                this.ContractSplit.grossMediaTotalWithBonus) *
                100
            );

      //  折扣金额(除赠送 SA)
      this.ContractSplit.discountTotalWithBonusSA = convertDecimal(
        this.ContractSplit.grossMediaTotal -
          MediaTotalSA -
          this.ContractSplit.agencyDiscountTotal
      );

      //  折扣金额(除赠送 SA)
      this.ContractSplit.discountTotalSA = convertDecimal(
        this.ContractSplit.grossMediaTotalWithBonus -
          BonusMediaTotalSA -
          this.ContractSplit.agencyDiscountTotal
      );

      // 折扣(SA)
      this.ContractSplit.discountRateWithBonusSA =
        this.ContractSplit.grossMediaTotal === 0
          ? 0.0
          : convertDecimal(
              (this.ContractSplit.discountTotalWithBonusSA /
                this.ContractSplit.grossMediaTotal) *
                100
            );
      this.ContractSplit.discountRateSA =
        this.ContractSplit.grossMediaTotalWithBonus === 0
          ? 0.0
          : convertDecimal(
              (this.ContractSplit.discountTotalSA /
                this.ContractSplit.grossMediaTotalWithBonus) *
                100
            );
    },
    /**
     * 计算制作总金额
     */
    totalProductionAmount() {
      const productions = this.ContractSplitMedias.filter(
        (t) => !t.isBonus
      )?.map((t) => parseFloat(t.totalProductionCost));
      const bonusProductions = this.ContractSplitMedias.filter(
        (t) => t.isBonus
      )?.map((t) => parseFloat(t.totalProductionCost));

      // 生产总金额(除赠送)
      this.ContractSplit.productionTotal = convertDecimal(
        productions.length === 0
          ? 0
          : productions.reduce((pre, curr) => pre + curr) || 0
      );

      // 生产总金额(赠送)
      this.ContractSplit.productionBonusTotal = convertDecimal(
        bonusProductions.length === 0
          ? 0
          : bonusProductions.reduce((pre, curr) => pre + curr)
      );

      // 制作费摘要
      this.ContractSplit.piCost = this.ContractSplit.productionTotalWithBonus =
        convertDecimal(
          this.ContractSplit.productionTotal +
            this.ContractSplit.productionBonusTotal
        );
    },
    /**
     * 计算合同金额
     */
    totalContractAmount() {
      const useContractInfo = useContractInfoStore();

      var MediaSum = 0;
      var ProductionSum = 0;
      var salesServiceFee = parseFloat(this.ContractSplit.salesServiceFee);
      var additionalProduction = parseFloat(
        this.ContractSplit.additionalProduction
      );
      // 1!=1代表Internal Split Total 显示
      if (
        useContractInfo.Contract.contractType === 'media&production' &&
        1 != 1
      ) {
        MediaSum = this.ContractSplitMedias.map((t) =>
          parseFloat(t.mediaTotalInternalSplit)
        ).reduce((pre, curr) => pre + curr);
        ProductionSum = this.ContractSplitMedias.map(
          (t) => parseFloat(t.productionTotalInternalSplit) || 0
        ).reduce((pre, curr) => pre + curr);
      } else {
        MediaSum = parseFloat(this.ContractSplit.mediaNett);
        ProductionSum = parseFloat(this.ContractSplit.piCost);
      }

      MediaSum =
        MediaSum - parseFloat(this.ContractSplit.specialDiscountAmount);

      this.ContractSplit.contractMediaAmount = convertDecimal(MediaSum);
      this.ContractSplit.contractProductionAmount = convertDecimal(
        ProductionSum + salesServiceFee + additionalProduction
      );
      this.ContractSplit.contractTotalAmount = convertDecimal(
        this.ContractSplit.contractMediaAmount +
          this.ContractSplit.contractProductionAmount
      );
    },
    /**
     * 计算佣金金额
     */
    totalCommissionAmount() {
      this.ContractSplit.specialComMediaAmount =
        this.ContractSplitMedias.length === 0
          ? 0
          : this.ContractSplitMedias.map((t) =>
              convertDecimal(t.commissionMediaCost)
            )?.reduce((pre, curr) => pre + curr);

      this.ContractSplit.specialComProductionAmount =
        this.ContractSplitMedias.length === 0
          ? 0
          : this.ContractSplitMedias.map((t) =>
              convertDecimal(t.commissionProductionCost)
            )?.reduce((pre, curr) => pre + curr);
    },

    // totalCommissionAmount () {
    //   this.ContractSplit.specialComMediaAmount =
    //     this.SpecialCommissionList.length === 0
    //       ? 0
    //       : this.SpecialCommissionList.map((t) => t.mediaCost)?.reduce(
    //         (pre, curr) => pre + curr
    //       );

    //   this.ContractSplit.specialComProductionAmount =
    //     this.SpecialCommissionList.length === 0
    //       ? 0
    //       : this.SpecialCommissionList.map((t) => t.productionCost)?.reduce(
    //         (pre, curr) => pre + curr
    //       );
    // },
    /**
     * 移除媒体
     * @param {*} index 第几个媒体
     */
    deleteMedia(index) {
      const SplitMediaId = this.ContractSplitMedias[index].splitMediaId;

      // 移除媒体
      this.ContractSplitMedias.splice(index, 1);

      // 移除制作费分拆
      const ProductionSplit =
        this.ContractProductionSplits.find(
          (t) => t.splitMediaId == SplitMediaId
        ) || null;
      if (ProductionSplit !== null) {
        const prodIndex =
          this.ContractProductionSplits.indexOf(ProductionSplit);
        this.ContractProductionSplits.splice(prodIndex, 1);
      }

      // 移除特殊佣金安排
      const SpecialCommission =
        this.SpecialCommissionList.find(
          (t) => t.splitMediaId == SplitMediaId
        ) || null;
      if (SpecialCommission !== null) {
        const commissionIndex =
          this.SpecialCommissionList.indexOf(SpecialCommission);
        this.SpecialCommissionList.splice(commissionIndex, 1);
      }

      this.totalContractAmount();

      this.totalMediaAmount();

      this.totalProductionAmount();

      this.totalCommissionAmount();
    },
    /**
     * 制作费用类型
     */
    async getProductionCostOptions() {
      this.ProductionCostOptions = await basicnet
        .getProductionCostOptionals()
        .then((res) => res.data);
    },
    async getSalesServiceOptions() {
      this.SalesServiceTypeOptions = await basicnet
        .getSalesServiceFeeOptionals()
        .then((res) => res.data);
    },
    async validate(type, index, action) {
      var dataType = type || 'split';

      var rules = IntlSplitRules.contractSplitRules;

      var data = this.ContractSplit;
      var source = Object.assign(data, {
        mediaSize: this.ContractSplitMedias.length,
      });
      if (dataType === 'media') {
        rules = IntlSplitRules.contractSplitMediaRules;
        data = this.ContractSplitMedias[index];
        source = { ...data };
      } else if (dataType === 'commission') {
        rules = IntlSplitRules.specialCommissionRules;
        data = this.SpecialCommissionList[index];
        source = { ...data };
      }
      source.action = action || '';

      var error = await validation.validate(source, rules).then((err) => err);

      data.validate = error;

      // 删除属性
      Reflect.deleteProperty(source, 'action');
    },
    NewProductionSplit(data) {
      var model = {
        code: data?.code || data?.Code || '',
        label: data?.label || data?.Label || '',
        amount: data?.amount || data?.Amount || 0,
        amountSum: data?.amountSum || data?.AmountSum || 0,
        description: data?.description || data?.Description || '',
        qty: data?.qty || data.Qty || 0,
        sort: data?.sort || data.Sort || 0,
      };

      var keys = Object.keys(data).filter(
        (t) => !Object.keys(model).includes(t.toLowerCase())
      );
      var newData = {};
      if (keys && keys.length > 0) {
        keys.map((t) => {
          const newPropertyName = t.replace(
            t.substring(0, 1),
            t.substring(0, 1).toLowerCase()
          );
          newData[newPropertyName] = data[t];
        });
      }

      model = Object.assign(model, newData);

      return model;
    },
    /**
     * 检查合同媒体的日期范围
     * 该函数用于验证合同媒体的开始日期和结束日期是否与原始媒体明细中的日期一致
     * 如果日期不一致，提示用户重新检查活动期限
     * 
     * @returns {string} 如果日期范围不一致，返回提示信息；否则返回空字符串
     */
    async MinMaxDateInternalSplit() {
      //修改media日期后,Internal Split/PaymentSchedule页面保存是否需要跟着修改
      if (!useContractStore().otherData.enableMinMaxMediaDate) {
        return true;
      }
      const useContractMedia = useMediaItemsStore();
      if (
        !useContractMedia.OrderMediaItems ||
        useContractMedia.OrderMediaItems.length < 1
      ) {
        await  useContractMedia.RefreshMediaItems();
      }
      // 重置分拆媒体
      const MediaItems = useContractMedia.OrderMediaItems || []; // 原来媒体明细
      if (
        !this.ContractSplitMedias ||
        this.ContractSplitMedias.length < 1
      ) {
        await this.RefreshInternalSplit();
      }
      
      const ContractSplitMedias = this.ContractSplitMedias || [];
      console.log(222222,MediaItems,ContractSplitMedias)

      // 遍历原始媒体明细
      for (const item of MediaItems) {
        // 过滤出与当前媒体项相关的分拆媒体
        const filteredMedias = ContractSplitMedias.filter(m => m.contractMediaId === item.id);

        // 如果没有匹配项，跳过当前循环
        if (filteredMedias.length === 0) {
          continue;
        }

        // 计算分拆媒体中的最小开始日期
        const minStartDate = filteredMedias.reduce((min, current) => {
          const currentDate = new Date(current.startDate);
          return isNaN(currentDate) ? min : (min < currentDate ? min : currentDate);
        }, new Date('9999-12-31'));
        // 计算分拆媒体中的最大结束日期
        const maxEndDate = filteredMedias.reduce((max, current) => {
          const currentDate = new Date(current.endDate);
          return isNaN(currentDate) ? max : (currentDate > max ? currentDate : max);
        }, new Date(0));
        console.log(111111112,minStartDate,maxEndDate)
        var util = require('@/utilFunction');
        // 打印日期信息用于调试
         console.log('33333', util.getDateFormat(item.startDate), util.getDateFormat(minStartDate), util.getDateFormat(item.endDate), util.getDateFormat(maxEndDate));

        // 如果日期范围不一致，返回提示信息
        if (util.getDateFormat(minStartDate) != util.getDateFormat(item.startDate) || util.getDateFormat(maxEndDate) != util.getDateFormat(item.endDate)) {
          return false;
        }
      }
      // 如果所有日期范围一致，返回空字符串
      return true;
    },
  },
});
